import { formatAmount } from "../../include/money";
import { useCallback, useContext, useEffect, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DataContext } from "../../context/DataContext";
import { canAdd, canEdit } from "../../include/staffAccess";
import { clubPost } from "./clubApi";
import { consoleColors, primaryButtonSx } from "../admin/consoleTheme";
import { hasEnded, indiaDate } from "../../include/indiaDate";
import { LaunchChooser, LaunchTimeFields, resizeLaunchTimes } from "../admin/LaunchTimes";

const emptyForm = {
  draw_id: "",
  title: "",
  frequency: "hour",
  start_at: "",
  end_at: "",
  min_sale_amount: "",
  print_no: "",
  background_image: "",
  back_image: "",
  banner: "",
};

function toLocal(value) {
  return String(value || "").replace(" ", "T").slice(0, 16);
}

function readImage(file) {
  return new Promise((resolve, reject) => {
    if (!file) return resolve("");
    if (file.size > 900000) return reject(new Error("Use an image under 900 KB"));
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read the image"));
    reader.readAsDataURL(file);
  });
}

export default function ClubDraws() {
  const navigate = useNavigate();
  const { clubData } = useContext(DataContext);
  const clubId = clubData?.club_info?.club_id;
  const [draws, setDraws] = useState([]);
  const [chooser, setChooser] = useState(null);

  const load = useCallback(async () => {
    if (!clubId) return;
    const result = await clubPost("DRAW_LIST", { club_id: clubId });
    if (result.status === "200") setDraws(result.data?.draws || []);
  }, [clubId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (draw) => {
    const result = await clubPost("DRAW_STATUS", { club_id: clubId, draw_id: draw.draw_id, status: draw.status === "1" ? "0" : "1" });
    if (result.status === "200") {
      toast.success(result.data?.message);
      load();
    } else toast.info(result.status_message || result.data?.error || "Could not update");
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Typography sx={{ color: consoleColors.muted, fontSize: 14 }}>Lucky Draw Coupons for every store in this club. Launch the draw from here.</Typography>
        {canAdd(clubData) && <Button variant="contained" sx={primaryButtonSx} onClick={() => navigate("/club/luckydraw/new")}><Add sx={{ mr: 0.5 }} /> Create Lucky Draw Coupons</Button>}
      </Box>

      {draws.length === 0 && <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 4, textAlign: "center", color: consoleColors.muted }}>No Lucky Draw Coupons yet.</Box>}

      <Box sx={{ display: "grid", gap: 1.5 }}>
        {draws.map((draw) => {
          const ended = Boolean(draw.ended) || hasEnded(draw.end_at);
          const live = !ended && draw.status === "1";
          return (
          <Box key={draw.draw_id} sx={{ background: live ? "#fff" : "#f6eaea", border: `1px solid ${live ? consoleColors.line : "#e7cfcf"}`, borderRadius: 2.5, p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <QRCodeCanvas value={draw.draw_id} size={72} />
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{draw.title}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.4 }}>{draw.frequency_label} · {indiaDate(draw.start_at)} to {indiaDate(draw.end_at)}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.ink, mt: 0.4 }}>Draw {draw.print_no ? Number(draw.print_no) : "-"} · Minimum sale ₹{formatAmount(draw.min_sale_amount)} · {draw.issued_count || 0} issued · {draw.winner_count || 0} winners · {ended ? "Ended" : live ? "Enabled" : "Disabled"}</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button size="small" variant="contained" sx={primaryButtonSx} onClick={() => setChooser(draw)}>Launch draw</Button>
              {canEdit(clubData) && <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/club/luckydraw/${draw.draw_id}/edit`)}>Edit</Button>}
              <Button size="small" sx={{ textTransform: "none" }} disabled={ended} onClick={() => toggle(draw)}>{live ? "Disable" : "Enable"}</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/club/luckydraw/${draw.draw_id}?view=result`)}>Result</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/club/luckydraw/${draw.draw_id}?view=issued`)}>Issued</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/club/luckydraw/${draw.draw_id}?view=winners`)}>Winners</Button>
            </Box>
          </Box>
          );
        })}
      </Box>
      <LaunchChooser
        open={Boolean(chooser)}
        title={chooser?.title || ""}
        frequencyLabel={chooser?.frequency_label}
        launches={chooser?.launches || []}
        loadSlots={(resultMode) => clubPost("DRAW_SLOTS", { club_id: clubId, draw_id: chooser.draw_id, result_mode: resultMode })}
        onClose={() => setChooser(null)}
        onOpen={(slot) => navigate(`/club/luckydraw/${chooser.draw_id}/launch?slot=${slot.index}`)}
        onOpenFrequency={(resultMode, slot) => navigate(`/club/luckydraw/${chooser.draw_id}/launch?mode=${resultMode}&key=${encodeURIComponent(slot.key)}`)}
        onShare={async (payload) => {
          const result = await clubPost("LAUNCH_LINK", { club_id: clubId, kind: "coupon", draw_id: chooser.draw_id, ...payload });
          if (result.status !== "200") {
            toast.info(result.status_message || result.data?.error || "Could not make the launch link");
            return "";
          }
          return `${window.location.origin}${result.data.path}`;
        }}
      />
    </Box>
  );
}

export function ClubDrawForm() {
  const { drawId } = useParams();
  const navigate = useNavigate();
  const { clubData } = useContext(DataContext);
  const clubId = clubData?.club_info?.club_id;
  const [form, setForm] = useState({ ...emptyForm, launch_count: "1", launch_times: [""], launch_locked: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!drawId || !clubId) return;
    clubPost("DRAW_DETAIL", { club_id: clubId, draw_id: drawId }).then((result) => {
      if (result.status !== "200") return;
      const full = result.data?.draw || {};
      setForm({
        draw_id: full.draw_id || drawId,
        title: full.title || "",
        frequency: full.frequency || "hour",
        start_at: toLocal(full.start_at),
        end_at: toLocal(full.end_at),
        min_sale_amount: full.min_sale_amount ?? "",
        print_no: full.print_no || "",
        background_image: full.background_image || "",
        back_image: full.back_image || "",
        banner: full.banner || "",
        launch_count: String(Math.max(1, (full.launches || []).length || 1)),
        launch_times: resizeLaunchTimes((full.launches || []).map((slot) => toLocal(slot.at)), Math.max(1, (full.launches || []).length || 1)),
        launch_locked: (full.launches || []).map((slot) => slot.launched === "1"),
      });
    });
  }, [drawId, clubId]);

  const onFile = async (key, file) => {
    try {
      const image = await readImage(file);
      setForm((prev) => ({ ...prev, [key]: image }));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const result = await clubPost("DRAW_SAVE", {
        ...form,
        club_id: clubId,
        scope: "club",
        min_sale_amount: Number(form.min_sale_amount || 0),
        launch_times: form.launch_times,
      });
      if (result.status === "200") {
        toast.success(result.data?.message || "Saved");
        navigate("/club/luckydraw");
      } else toast.info(result.status_message || result.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 760 }}>
      <Button sx={{ textTransform: "none", mb: 2 }} onClick={() => navigate("/club/luckydraw")}>Back to Lucky Draw Coupons</Button>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 750 }}>{drawId ? "Edit Lucky Draw Coupons" : "Create Lucky Draw Coupons"}</Typography>
        <TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <TextField select label="Lucky Draw Frequency" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
          <MenuItem value="hour">Every 1 hour</MenuItem>
          <MenuItem value="day">Every day</MenuItem>
          <MenuItem value="all">All entries</MenuItem>
        </TextField>
        <TextField label="Starts" type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} InputLabelProps={{ shrink: true }} />
        <TextField label="Ends" type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} InputLabelProps={{ shrink: true }} />
        <TextField label="Minimum sale amount" type="number" value={form.min_sale_amount} onChange={(e) => setForm({ ...form, min_sale_amount: e.target.value })} helperText="A coupon is issued when the sale is at least this amount." />
        <TextField label="Draw number" value={form.print_no} onChange={(e) => setForm({ ...form, print_no: e.target.value.replace(/\D/g, "").slice(0, 2) })} helperText="1, 2, 3… printed in the coupon barcode. Leave blank to assign the next number." inputProps={{ inputMode: "numeric", maxLength: 2 }} />
        <LaunchTimeFields
          count={form.launch_count}
          times={form.launch_times}
          locked={form.launch_locked}
          onCount={(value) => {
            const count = Math.min(30, Math.max(1, Number(value) || 1));
            const kept = (form.launch_locked || []).filter(Boolean).length;
            if (count < kept) return;
            setForm({ ...form, launch_count: String(count), launch_times: resizeLaunchTimes(form.launch_times, count) });
          }}
          onTime={(index, value) => {
            const launch_times = form.launch_times.slice();
            launch_times[index] = value;
            setForm({ ...form, launch_times });
          }}
        />
        <Button component="label" sx={{ textTransform: "none", justifyContent: "flex-start" }}>
          Front image {form.background_image ? "· selected" : ""}
          <input hidden type="file" accept="image/*" onChange={(e) => onFile("background_image", e.target.files?.[0])} />
        </Button>
        {form.background_image && <Box component="img" src={form.background_image} alt="Front" sx={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 1 }} />}
        <Button component="label" sx={{ textTransform: "none", justifyContent: "flex-start" }}>
          Back image (optional) {form.back_image ? "· selected" : ""}
          <input hidden type="file" accept="image/*" onChange={(e) => onFile("back_image", e.target.files?.[0])} />
        </Button>
        {form.back_image && <Box component="img" src={form.back_image} alt="Back" sx={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 1 }} />}
        <Button component="label" sx={{ textTransform: "none", justifyContent: "flex-start" }}>
          Banner {form.banner ? "· selected" : ""}
          <input hidden type="file" accept="image/*" onChange={(e) => onFile("banner", e.target.files?.[0])} />
        </Button>
        {form.banner && <Box component="img" src={form.banner} alt="Banner" sx={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 1 }} />}
        {form.draw_id && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <QRCodeCanvas value={form.draw_id} size={84} />
            <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>QR for this draw. Code {form.draw_id}</Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button onClick={() => navigate("/club/luckydraw")} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" sx={primaryButtonSx} disabled={saving} onClick={save}>{saving ? "Saving..." : "Save"}</Button>
        </Box>
      </Box>
    </Box>
  );
}
